import { useRef, useState } from 'react';
import { PencilLine, Highlighter, Circle, ArrowUpRight, StickyNote, Square, Undo2, Redo2, Eraser, Trash2, Hand, Aperture } from 'lucide-react';
import { Tooltip } from '../primitives/Tooltip';
import { Popover } from '../primitives/Popover';
import { annotationColors, highlights, type HighlightColor } from '@vd/tokens';
import { cn } from '../cn';
import styles from './AnnotBar.module.css';

export type AnnotTool = 'pan' | 'pen' | 'highlighter' | 'circle' | 'arrow' | 'text' | 'redact' | 'blur' | 'eraser';

export interface AnnotBarProps {
  tool: AnnotTool;
  color: HighlightColor;
  onSelectTool: (tool: AnnotTool) => void;
  onSelectColor: (color: HighlightColor) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onClear?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  canClear?: boolean;
  className?: string;
}

const tools: { key: AnnotTool; label: string; shortcut: string; icon: typeof PencilLine }[] = [
  { key: 'pan',         label: 'Pan (drag image)', shortcut: 'V', icon: Hand },
  { key: 'pen',         label: 'Pen',              shortcut: 'P', icon: PencilLine },
  { key: 'highlighter', label: 'Highlighter',      shortcut: 'H', icon: Highlighter },
  { key: 'circle',      label: 'Circle',           shortcut: 'C', icon: Circle },
  { key: 'arrow',       label: 'Arrow',            shortcut: 'A', icon: ArrowUpRight },
  { key: 'text',        label: 'Text',             shortcut: 'T', icon: StickyNote },
  { key: 'redact',      label: 'Redact',           shortcut: 'R', icon: Square },
  // Blur sits next to redact because they're the same shape (rect)
  // with different fill behaviors — redact replaces the underlying
  // image with a solid black block; blur preserves the shape but
  // softens it so a viewer can see SOMETHING is there without
  // reading the content. Common use: obscure faces or names where
  // a black box would look heavy-handed.
  { key: 'blur',        label: 'Blur',             shortcut: 'B', icon: Aperture },
  { key: 'eraser',      label: 'Eraser',           shortcut: 'E', icon: Eraser },
];

export function AnnotBar({
  tool, color, onSelectTool, onSelectColor,
  onUndo, onRedo, onClear, canUndo, canRedo, canClear,
  className,
}: AnnotBarProps) {
  // Color picker is collapsed into a single swatch button to keep the
  // toolbar narrow on smaller monitors (the inline row of swatches
  // used to push undo/redo/clear off the right edge). Clicking the
  // swatch opens a popover with the full palette.
  const [colorOpen, setColorOpen] = useState(false);
  const colorBtnRef = useRef<HTMLButtonElement>(null);
  const currentColorLabel = color.charAt(0).toUpperCase() + color.slice(1);

  return (
    <div className={cn(styles.bar, className)} role="toolbar" aria-label="Annotation tools">
      {tools.map((t) => {
        const Icon = t.icon;
        const active = tool === t.key;
        return (
          <Tooltip key={t.key} content={`${t.label} (${t.shortcut})`}>
            <button
              className={cn(styles.tool, active && styles.active)}
              onClick={() => onSelectTool(t.key)}
              aria-pressed={active}
              aria-label={t.label}
            >
              <Icon size={14} />
            </button>
          </Tooltip>
        );
      })}

      <span className={styles.sep} />

      <Tooltip content={`Color · ${currentColorLabel}`}>
        <button
          ref={colorBtnRef}
          className={cn(styles.colorTrigger, colorOpen && styles.active)}
          style={{ background: highlights[color] }}
          onClick={() => setColorOpen((v) => !v)}
          aria-label={`Color: ${currentColorLabel}. Click to change.`}
          aria-haspopup="dialog"
          aria-expanded={colorOpen}
        />
      </Tooltip>
      <Popover
        open={colorOpen}
        onClose={() => setColorOpen(false)}
        anchor={colorBtnRef.current?.getBoundingClientRect() ?? null}
        placement="top-start"
      >
        <div className={styles.colorPalette} role="listbox" aria-label="Annotation color">
          {annotationColors.map((c) => (
            <Tooltip key={c} content={c.charAt(0).toUpperCase() + c.slice(1)}>
              <button
                className={cn(styles.color, color === c && styles.colorActive)}
                style={{ background: highlights[c] }}
                onClick={() => { onSelectColor(c); setColorOpen(false); }}
                aria-label={`Color ${c}`}
                aria-pressed={color === c}
                role="option"
                aria-selected={color === c}
              />
            </Tooltip>
          ))}
        </div>
      </Popover>

      <span className={styles.sep} />

      <Tooltip content="Undo (⌘Z)">
        <button className={styles.tool} onClick={onUndo} disabled={!canUndo} aria-label="Undo">
          <Undo2 size={14} />
        </button>
      </Tooltip>
      <Tooltip content="Redo (⌘⇧Z)">
        <button className={styles.tool} onClick={onRedo} disabled={!canRedo} aria-label="Redo">
          <Redo2 size={14} />
        </button>
      </Tooltip>
      <Tooltip content="Clear all annotations">
        <button
          className={cn(styles.tool, styles.clearBtn)}
          onClick={onClear}
          disabled={!canClear}
          aria-label="Clear all annotations"
        >
          <Trash2 size={14} />
        </button>
      </Tooltip>
    </div>
  );
}
